import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './HoverCard.css';
import { useChatDock } from '../contexts/ChatDockContext';
import supabase from '../supabaseClient';

/**
 * HoverCard component for profiles, groups, and events
 * Shows a preview card on hover with relevant information and CTA links
 */
const HoverCard = ({ type, data, children, disabled = false, currentUserId = null, context = null }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [fullProfileData, setFullProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const triggerRef = useRef(null);
  const cardRef = useRef(null);
  const timeoutRef = useRef(null);
  const fetchedRef = useRef(false);
  const { openChat } = useChatDock?.() || { openChat: null };

  // Fetch full profile data when card becomes visible (for profile type)
  useEffect(() => {
    const fetchProfile = async () => {
      if (isVisible && type === 'profile' && data?.id && !fetchedRef.current) {
        fetchedRef.current = true;
        setIsLoading(true);

        console.log('[HoverCard] Fetching profile for:', data.id);

        try {
          const { data: profileData, error } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url, bio, location, native_languages, target_languages')
            .eq('id', data.id)
            .single();

          console.log('[HoverCard] Profile fetch result:', { profileData, error });

          setIsLoading(false);
          if (!error && profileData) {
            const merged = {
              ...data,
              ...profileData
            };
            console.log('[HoverCard] Setting fullProfileData:', merged);
            setFullProfileData(merged);
          } else {
            console.log('[HoverCard] Fetch failed, using passed data');
            setFullProfileData(data);
          }
        } catch (err) {
          console.error('[HoverCard] Fetch error:', err);
          setIsLoading(false);
          setFullProfileData(data);
        }
      }
    };

    fetchProfile();
  }, [isVisible, type, data]);

  // Reset fetch state when user changes
  useEffect(() => {
    fetchedRef.current = false;
    setFullProfileData(null);
  }, [data?.id]);

  const handleMouseEnter = () => {
    if (disabled) return;
    // Clear any pending hide timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Show card after 300ms delay
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      updatePosition();
    }, 300);
  };

  const handleMouseLeave = () => {
    // Clear show timeout if still pending
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Delay hiding the card by 800ms to allow mouse movement
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 800);
  };

  const handleCardMouseEnter = () => {
    // Cancel hide timeout when mouse enters the card
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(true);
  };

  const handleCardMouseLeave = () => {
    // Hide immediately when leaving the card
    setIsVisible(false);
  };

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const cardWidth = 380;
    const cardHeight = 280; // Increased to accommodate more content
    
    let top = rect.bottom + 8;
    let left = rect.left;
    
    // Check if card would go off-screen to the right
    if (left + cardWidth > window.innerWidth - 20) {
      left = window.innerWidth - cardWidth - 20;
    }
    
    // Check if card would go off-screen at the bottom
    if (top + cardHeight > window.innerHeight - 20) {
      top = rect.top - cardHeight - 8;
    }
    
    setPosition({ top, left });
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const renderCard = () => {
    if (!data) return null;

    switch (type) {
      case 'profile':
        // Use fetched full profile data if available, otherwise use passed data
        const profileData = fullProfileData || data;
        console.log('[HoverCard] Rendering with profileData:', { fullProfileData, profileData, hasBio: !!profileData?.bio, hasLocation: !!profileData?.location });
        const isOwnProfile = currentUserId && profileData.id === currentUserId;
        const isConnected = profileData.is_connected || false;
        return (
          <div className="hover-card-content">
            {isLoading && (
              <div className="hover-card-loading">Loading...</div>
            )}
            {!isOwnProfile && (
              <a
                href={isConnected ? '#' : `/app/profile/${profileData.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`hover-card-connect-btn ${isConnected ? 'connected' : ''}`}
                title={isConnected ? 'Connected' : 'Connect'}
                onClick={(e) => isConnected && e.preventDefault()}
              >
                {isConnected ? 'Connected' : 'Connect'}
              </a>
            )}
            <div className="hover-card-header">
              <img
                src={profileData.avatar_url || '/avatar-placeholder.png'}
                alt={profileData.display_name || profileData.name}
                className="hover-card-avatar"
              />
              <div className="hover-card-info">
                <a
                  href={`/app/profile/${profileData.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-card-name-link"
                >
                  <h3 className="hover-card-name">{profileData.display_name || profileData.name || 'User'}</h3>
                </a>
                {profileData.bio && <p className="hover-card-bio">{profileData.bio.slice(0, 120)}{profileData.bio.length > 120 ? '...' : ''}</p>}
                <div className="hover-card-details">
                  {profileData.location && (
                    <p className="hover-card-meta">📍 {profileData.location}</p>
                  )}
                  {profileData.native_languages && Array.isArray(profileData.native_languages) && profileData.native_languages.length > 0 && (
                    <p className="hover-card-meta">🗣️ Native: {profileData.native_languages.map(l => typeof l === 'object' ? l.label : l).join(', ')}</p>
                  )}
                  {profileData.target_languages && Array.isArray(profileData.target_languages) && profileData.target_languages.length > 0 && (
                    <p className="hover-card-meta">🎯 Learning: {profileData.target_languages.map(l => typeof l === 'object' ? l.label : l).join(', ')}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="hover-card-actions">
              <a
                href={`/app/profile/${profileData.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover-card-btn primary"
              >
                Visit Profile
              </a>
              {context !== 'messenger' && (
                <button
                  type="button"
                  className="hover-card-btn secondary"
                  onClick={(e) => { e.preventDefault(); if (openChat) openChat(profileData.id); }}
                >
                  Message
                </button>
              )}
            </div>
          </div>
        );

      case 'group':
        const isMemberOrCreator = data.is_member || (data.created_by && data.created_by === currentUserId) || false;
        return (
          <div className="hover-card-content">
            <div className="hover-card-header">
              {data.group_banner_url && (
                <img
                  src={data.group_banner_url}
                  alt={data.name}
                  className="hover-card-banner"
                />
              )}
              <div className="hover-card-info">
                <a
                  href={`/app/groups/${data.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-card-name-link"
                >
                  <h3 className="hover-card-name">{data.name || 'Group'}</h3>
                </a>
                {data.description && (
                  <p className="hover-card-bio">{data.description.slice(0, 120)}{data.description.length > 120 ? '...' : ''}</p>
                )}
                <div className="hover-card-details">
                  {data.language && (
                    <p className="hover-card-meta">🌐 {data.language}</p>
                  )}
                  {data.level && (
                    <p className="hover-card-meta">📊 Level: {data.level}</p>
                  )}
                  {data.members && (
                    <p className="hover-card-meta">👥 {Array.isArray(data.members) ? data.members.length : data.members} members</p>
                  )}
                </div>
              </div>
            </div>
            <div className="hover-card-actions">
              <a
                href={`/app/groups/${data.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover-card-btn primary"
              >
                Visit Group
              </a>
              {context !== 'messenger' && (
                <a
                  href={isMemberOrCreator ? '#' : `/app/groups/${data.id}/join`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hover-card-btn secondary ${isMemberOrCreator ? 'joined' : ''}`}
                  onClick={(e) => isMemberOrCreator && e.preventDefault()}
                >
                  {isMemberOrCreator ? 'Joined' : 'Join'}
                </a>
              )}
            </div>
          </div>
        );

      case 'event':
        const isAttendingOrCreator = data.is_attending || (data.created_by && data.created_by === currentUserId) || false;
        return (
          <div className="hover-card-content">
            <div className="hover-card-header">
              {data.event_banner_url && (
                <img
                  src={data.event_banner_url}
                  alt={data.title}
                  className="hover-card-banner"
                />
              )}
              <div className="hover-card-info">
                <a
                  href={`/app/events/${data.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-card-name-link"
                >
                  <h3 className="hover-card-name">{data.title || 'Event'}</h3>
                </a>
                {data.description && (
                  <p className="hover-card-bio">{data.description.slice(0, 120)}{data.description.length > 120 ? '...' : ''}</p>
                )}
                <div className="hover-card-details">
                  {data.language && (
                    <p className="hover-card-meta">🌐 {data.language}</p>
                  )}
                  {data.level && (
                    <p className="hover-card-meta">📊 Level: {data.level}</p>
                  )}
                  {data.date && (
                    <p className="hover-card-meta">📅 {new Date(data.date).toLocaleDateString()} {data.time || ''}</p>
                  )}
                  {data.location && (
                    <p className="hover-card-meta">📍 {data.location}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="hover-card-actions">
              <a
                href={`/app/events/${data.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover-card-btn primary"
              >
                See Event
              </a>
              <a
                href={isAttendingOrCreator ? '#' : `/app/events/${data.id}/join`}
                target="_blank"
                rel="noopener noreferrer"
                className={`hover-card-btn secondary ${isAttendingOrCreator ? 'joined' : ''}`}
                onClick={(e) => isAttendingOrCreator && e.preventDefault()}
              >
                {isAttendingOrCreator ? 'Joined' : 'Join'}
              </a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="hover-card-trigger"
      >
        {children}
      </span>
      {isVisible && createPortal(
        <div
          ref={cardRef}
          className="hover-card-overlay"
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
        >
          {renderCard()}
        </div>,
        document.body
      )}
    </>
  );
};

export default HoverCard;
